// frontend/src/pages/MarketingCopyPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  FindReplace as FindReplaceIcon,
  Preview as PreviewIcon,
  CloudUpload as UploadIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { previewMarketingCopy, findReplaceMarketingCopy, getMarketingCopyFields } from '../api/marketingCopy';

const MarketingCopyPage = () => {
  // State for UPC input
  const [upcInput, setUpcInput] = useState('');
  const [upcs, setUpcs] = useState([]);

  // State for find/replace
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [selectedField, setSelectedField] = useState('Marketing Copy');
  const [caseSensitive, setCaseSensitive] = useState(false);

  // State for recipient-specific data (Kroger's party ID)
  const [recipientPartyId, setRecipientPartyId] = useState('723b8705-9f63-4698-bc50-b0d0b46c11c2');

  // State for available fields
  const [availableFields, setAvailableFields] = useState([]);

  // State for preview and results
  const [previewData, setPreviewData] = useState([]);
  const [findReplaceResults, setFindReplaceResults] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});

  const steps = ['Enter UPCs', 'Preview Marketing Copy', 'Find & Replace', 'Review & Apply'];

  // Fetch available fields on mount
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const result = await getMarketingCopyFields();
        setAvailableFields(result.fields || []);
      } catch (err) {
        console.error('Error fetching fields:', err);
      }
    };
    fetchFields();
  }, []);

  // Parse UPCs from input
  const parseUpcs = () => {
    const parsed = upcInput
      .split(/[\n,\s]+/)
      .map(upc => upc.trim())
      .filter(upc => upc.length > 0);
    setUpcs(parsed);
    return parsed;
  };

  // Handle preview
  const handlePreview = async () => {
    const parsedUpcs = parseUpcs();
    if (parsedUpcs.length === 0) {
      setError('Please enter at least one UPC');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Pass recipientPartyId for Kroger-specific marketing copy
      const results = await previewMarketingCopy(parsedUpcs, recipientPartyId || null);
      setPreviewData(results);
      setActiveStep(1);
    } catch (err) {
      setError(err.message || 'Failed to preview marketing copy');
    } finally {
      setLoading(false);
    }
  };

  // Handle find/replace preview
  const handleFindReplacePreview = async () => {
    if (!findText) {
      setError('Please enter text to find');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const results = await findReplaceMarketingCopy({
        upcs: upcs,
        find_text: findText,
        replace_text: replaceText,
        field: selectedField,
        case_sensitive: caseSensitive,
        preview_only: true,
        recipient_party_id: recipientPartyId || null
      });
      setFindReplaceResults(results);
      setActiveStep(3);
    } catch (err) {
      setError(err.message || 'Failed to perform find/replace preview');
    } finally {
      setLoading(false);
    }
  };

  // Handle actual find/replace
  const handleApplyChanges = async () => {
    if (!findReplaceResults || findReplaceResults.total_updated === 0) {
      setError('No changes to apply');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const results = await findReplaceMarketingCopy({
        upcs: upcs,
        find_text: findText,
        replace_text: replaceText,
        field: selectedField,
        case_sensitive: caseSensitive,
        preview_only: false,
        recipient_party_id: recipientPartyId || null
      });
      setFindReplaceResults(results);
      setSuccess(`Successfully updated ${results.total_updated} items`);
    } catch (err) {
      setError(err.message || 'Failed to apply changes');
    } finally {
      setLoading(false);
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (upc) => {
    setExpandedRows(prev => ({
      ...prev,
      [upc]: !prev[upc]
    }));
  };

  // Get status chip color
  const getStatusChip = (status) => {
    const statusConfig = {
      'found': { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
      'not_found': { color: 'warning', icon: <WarningIcon fontSize="small" /> },
      'error': { color: 'error', icon: <ErrorIcon fontSize="small" /> },
      'updated': { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
      'no_match': { color: 'default', icon: <WarningIcon fontSize="small" /> },
      'preview': { color: 'info', icon: <PreviewIcon fontSize="small" /> }
    };
    const config = statusConfig[status] || { color: 'default', icon: null };
    return (
      <Chip
        label={status.replace('_', ' ')}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  // Highlight matched text
  const highlightText = (text, find, replace) => {
    if (!text || !find) return text;

    const regex = new RegExp(`(${find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, caseSensitive ? 'g' : 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === find.toLowerCase()) {
        return (
          <span key={index} style={{ backgroundColor: '#ffeb3b', textDecoration: 'line-through' }}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // Reset form
  const handleReset = () => {
    setUpcInput('');
    setUpcs([]);
    setFindText('');
    setReplaceText('');
    setPreviewData([]);
    setFindReplaceResults(null);
    setActiveStep(0);
    setError(null);
    setSuccess(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Marketing Copy Find & Replace
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Search and replace text in Syndigo Marketing Copy fields for multiple UPCs
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Panel - Input */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                1. Enter UPCs
              </Typography>
              <TextField
                label="Recipient Party ID (for Kroger-specific data)"
                fullWidth
                value={recipientPartyId}
                onChange={(e) => setRecipientPartyId(e.target.value)}
                placeholder="723b8705-9f63-4698-bc50-b0d0b46c11c2"
                helperText="Leave default for Kroger Our Brands Item API data"
                sx={{ mb: 2 }}
                size="small"
              />
              <TextField
                label="UPCs (one per line or comma-separated)"
                multiline
                rows={6}
                fullWidth
                value={upcInput}
                onChange={(e) => setUpcInput(e.target.value)}
                placeholder="0001234567890&#10;0009876543210&#10;..."
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handlePreview}
                disabled={loading || !upcInput.trim()}
              >
                {loading ? <CircularProgress size={24} /> : 'Fetch Marketing Copy'}
              </Button>

              {upcs.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {upcs.length} UPC(s) loaded
                </Typography>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                2. Find & Replace
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Field</InputLabel>
                <Select
                  value={selectedField}
                  onChange={(e) => setSelectedField(e.target.value)}
                  label="Field"
                >
                  {availableFields.map((field) => (
                    <MenuItem key={field.name} value={field.name}>
                      {field.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Find Text"
                fullWidth
                value={findText}
                onChange={(e) => setFindText(e.target.value)}
                placeholder="Text to find..."
                sx={{ mb: 2 }}
              />

              <TextField
                label="Replace With"
                fullWidth
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
                placeholder="Replacement text..."
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={caseSensitive}
                    onChange={(e) => setCaseSensitive(e.target.checked)}
                  />
                }
                label="Case Sensitive"
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={<FindReplaceIcon />}
                onClick={handleFindReplacePreview}
                disabled={loading || previewData.length === 0 || !findText}
                sx={{ mb: 1 }}
              >
                Preview Changes
              </Button>

              <Button
                variant="outlined"
                fullWidth
                startIcon={<ClearIcon />}
                onClick={handleReset}
              >
                Reset
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={8}>
          {/* Preview Data Table */}
          {previewData.length > 0 && (
            <Paper sx={{ mb: 3 }}>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Marketing Copy Preview ({previewData.length} items)
                </Typography>
                <Box>
                  <Chip
                    label={`Found: ${previewData.filter(p => p.status === 'found').length}`}
                    color="success"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`Not Found: ${previewData.filter(p => p.status === 'not_found').length}`}
                    color="warning"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`Errors: ${previewData.filter(p => p.status === 'error').length}`}
                    color="error"
                    size="small"
                  />
                </Box>
              </Box>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="40"></TableCell>
                      <TableCell>UPC</TableCell>
                      <TableCell>Brand</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Marketing Copy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((item) => (
                      <React.Fragment key={item.upc}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpansion(item.upc)}
                            >
                              {expandedRows[item.upc] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {item.normalized_upc || item.upc}
                            </Typography>
                          </TableCell>
                          <TableCell>{item.brand || '-'}</TableCell>
                          <TableCell>{getStatusChip(item.status)}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {item.marketing_copy || item.long_item_description || item.error || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0 }}>
                            <Collapse in={expandedRows[item.upc]} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 4, bgcolor: 'grey.50' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Marketing Copy:
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap', bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                      {item.marketing_copy || 'N/A'}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Feature - Benefit Bullets:
                                    </Typography>
                                    <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                      {[1, 2, 3, 4, 5, 6, 7].map((num) => {
                                        const bulletKey = `feature_benefit_bullet_${num}`;
                                        const bulletValue = item[bulletKey];
                                        return bulletValue ? (
                                          <Typography key={num} variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>{num}.</strong> {bulletValue}
                                          </Typography>
                                        ) : null;
                                      })}
                                      {![1, 2, 3, 4, 5, 6, 7].some(num => item[`feature_benefit_bullet_${num}`]) && (
                                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                                      )}
                                    </Box>
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Long Item Description:
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                      {item.long_item_description || 'N/A'}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Package Marketing Text:
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                      {item.package_marketing_text || 'N/A'}
                                    </Typography>
                                  </Grid>
                                </Grid>

                                {item.error && (
                                  <Alert severity="error" sx={{ mt: 2 }}>
                                    {item.error}
                                  </Alert>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Find/Replace Results Table */}
          {findReplaceResults && (
            <Paper>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Find & Replace Results
                </Typography>
                <Box>
                  <Chip
                    label={`Matches: ${findReplaceResults.total_updated}`}
                    color="success"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`No Match: ${findReplaceResults.total_no_match}`}
                    color="default"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`Errors: ${findReplaceResults.total_errors}`}
                    color="error"
                    size="small"
                  />
                </Box>
              </Box>

              {findReplaceResults.preview_only && findReplaceResults.total_updated > 0 && (
                <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
                  This is a preview. Click "Apply Changes" to update Syndigo.
                </Alert>
              )}

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="40"></TableCell>
                      <TableCell>UPC</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Matches</TableCell>
                      <TableCell>Preview</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {findReplaceResults.results.map((item) => (
                      <React.Fragment key={item.upc}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpansion(`fr-${item.upc}`)}
                            >
                              {expandedRows[`fr-${item.upc}`] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {item.normalized_upc || item.upc}
                            </Typography>
                          </TableCell>
                          <TableCell>{getStatusChip(item.status)}</TableCell>
                          <TableCell>{item.matches_found}</TableCell>
                          <TableCell>
                            {item.matches_found > 0 && (
                              <Typography variant="body2" color="success.main">
                                "{findText}" -&gt; "{replaceText}"
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0 }}>
                            <Collapse in={expandedRows[`fr-${item.upc}`]} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 4, bgcolor: 'grey.50' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Original Value:
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                                  {item.original_value ? highlightText(item.original_value, findText, replaceText) : 'N/A'}
                                </Typography>
                                {item.new_value && (
                                  <>
                                    <Typography variant="subtitle2" gutterBottom>
                                      New Value:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: 'pre-wrap',
                                        bgcolor: 'success.light',
                                        p: 1,
                                        borderRadius: 1
                                      }}
                                    >
                                      {item.new_value}
                                    </Typography>
                                  </>
                                )}
                                {item.error && (
                                  <Alert severity="error" sx={{ mt: 2 }}>
                                    {item.error}
                                  </Alert>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {findReplaceResults.preview_only && findReplaceResults.total_updated > 0 && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<UploadIcon />}
                    onClick={handleApplyChanges}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : `Apply Changes (${findReplaceResults.total_updated} items)`}
                  </Button>
                </Box>
              )}

              {!findReplaceResults.preview_only && (
                <Box sx={{ p: 2 }}>
                  <Alert severity="success">
                    Changes have been applied to Syndigo. {findReplaceResults.total_updated} items updated successfully.
                  </Alert>
                </Box>
              )}
            </Paper>
          )}

          {/* Empty state */}
          {previewData.length === 0 && !findReplaceResults && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <FindReplaceIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Enter UPCs to get started
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Paste a list of UPCs to fetch and preview their marketing copy from Syndigo
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MarketingCopyPage;
